'use client';

import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { Download, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import type { ToolPartCustomRendererArgs } from '@/features/copilot/ui/Tool/ToolPart';
import { useCopilotToolUi } from '@/features/copilot/ui/ToolUi/useCopilotToolUi';
import {
  LexicalMarkdownEditor,
  TABLE_TRANSFORMER,
} from '@/shared/ui/primitives/lexical-markdown-editor';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { priorAuthKeys } from '@/entities/prior-auth';
import { SavedSignatureBlock, SignaturePanel, useSignatureQuery } from '@/features/signature';

import { useLmnDraftEdit } from '../model/useLmnDraftEdit';
import { publishLmnDraftStatus } from '../model/lmnDraftStatus';
import {
  checkLmnAttachAvailability,
  downloadLmnDocx,
  extractLmnErrorDetail,
  signAndAttachLmn,
} from '../api/lmnApi';

// Bound once so the editor doesn't re-initialise on every render (a fresh
// array identity remounts LexicalComposer and flashes the content).
const EDITOR_TABLE_NODES = [TableNode, TableRowNode, TableCellNode] as const;
const EDITOR_TABLE_TRANSFORMERS = [TABLE_TRANSFORMER] as const;

interface DraftOutputT {
  prior_auth_record_id: string;
  case_number: string;
  markdown: string;
}

function parseOutput(output: unknown): DraftOutputT | null {
  if (!output || typeof output !== 'object') return null;
  const o = output as Record<string, unknown>;
  if (
    typeof o.prior_auth_record_id !== 'string' ||
    typeof o.case_number !== 'string' ||
    typeof o.markdown !== 'string'
  ) {
    return null;
  }
  return {
    prior_auth_record_id: o.prior_auth_record_id,
    case_number: o.case_number,
    markdown: o.markdown,
  };
}

function parseCaseId(input: unknown): number | null {
  if (!input || typeof input !== 'object') return null;
  const raw =
    (input as Record<string, unknown>).prior_auth_id ??
    (input as Record<string, unknown>).priorAuthId;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
}

/** Renderer for the ``draft_prior_auth_letter`` tool part. Bridges the
 *  tool lifecycle to the PA wizard via ``$lmnDraftStatus`` and, once the
 *  draft is ready, mounts the inline editor card. */
export function DraftPriorAuthLetterToolCard({
  state,
  input,
  output,
  toolCallId,
}: ToolPartCustomRendererArgs) {
  const caseId = parseCaseId(input);

  useEffect(() => {
    if (caseId === null) return;
    if (state === 'input-streaming' || state === 'input-available') {
      publishLmnDraftStatus(caseId, 'inflight');
    } else if (state === 'output-available') {
      publishLmnDraftStatus(caseId, 'done');
    } else if (state === 'output-error') {
      publishLmnDraftStatus(caseId, 'error');
    }
  }, [caseId, state]);

  if (state !== 'output-available') return null;
  const parsed = parseOutput(output);
  if (parsed === null) return null;

  return <DraftEditorCard toolCallId={toolCallId} output={parsed} />;
}

function DraftEditorCard({ toolCallId, output }: { toolCallId: string; output: DraftOutputT }) {
  const { patientId } = useCopilotToolUi();
  const draft = useLmnDraftEdit({ patientId, toolCallId, initialMarkdown: output.markdown });
  const [attachOpen, setAttachOpen] = useState(false);

  // The backend owns the "can I attach here?" rules; we just render its
  // answer instead of duplicating the status checks client-side.
  const availability = useQuery({
    queryKey: ['lmnAttachAvailability', output.prior_auth_record_id],
    queryFn: ({ signal }) =>
      checkLmnAttachAvailability({ prior_auth_record_id: output.prior_auth_record_id }, signal),
    // Every past draft card in the thread mounts this gate; don't refetch
    // it on every window focus. A case rarely changes ownership within a
    // session, and sign-and-attach re-validates server-side regardless.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const download = useMutation({
    mutationFn: () =>
      downloadLmnDocx({
        title: `Letter of Medical Necessity — PA #${output.case_number}`,
        markdown: draft.getLatest(),
        filename: `letter-of-medical-necessity-pa-${output.case_number}.docx`,
      }),
    onError: (err) => toast.error(`Couldn't download the .docx: ${extractLmnErrorDetail(err)}`),
  });

  const isEmpty = draft.value.trim().length === 0;
  const blockedReason =
    availability.data?.would_accept === false ? availability.data.message : null;

  return (
    <div className="my-2 flex max-h-[75vh] flex-col gap-2 rounded-md border bg-card p-3 shadow-sm">
      <div className="shrink-0 text-sm font-medium">
        Letter of Medical Necessity — PA #{output.case_number}
      </div>

      <div className="flex min-h-[28rem] flex-1 flex-col overflow-hidden">
        <LexicalMarkdownEditor
          value={draft.value}
          onChange={draft.onChange}
          toolbar="basic"
          extraNodes={EDITOR_TABLE_NODES}
          extraTransformers={EDITOR_TABLE_TRANSFORMERS}
        />
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => download.mutate()}
          disabled={download.isPending || isEmpty}
        >
          {download.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Download .docx
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() => setAttachOpen(true)}
          disabled={isEmpty || availability.isLoading || blockedReason !== null}
          title={blockedReason ?? undefined}
        >
          <Send className="h-4 w-4" />
          Attach to PA
        </Button>
      </div>

      {blockedReason !== null ? (
        <div className="shrink-0 text-xs text-muted-foreground">{blockedReason}</div>
      ) : null}

      <AttachDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        priorAuthRecordId={output.prior_auth_record_id}
        caseNumber={output.case_number}
        getMarkdown={draft.getLatest}
      />
    </div>
  );
}

function AttachDialog({
  open,
  onOpenChange,
  priorAuthRecordId,
  caseNumber,
  getMarkdown,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priorAuthRecordId: string;
  caseNumber: string;
  getMarkdown: () => string;
}) {
  const queryClient = useQueryClient();
  const signatureQuery = useSignatureQuery();
  const [signaturePanelOpen, setSignaturePanelOpen] = useState(false);
  const signature = open ? (signatureQuery.data ?? null) : null;

  const attach = useMutation({
    mutationFn: () =>
      signAndAttachLmn({ prior_auth_record_id: priorAuthRecordId, markdown: getMarkdown() }),
    onSuccess: (result) => {
      toast.success(`Signed and attached to PA #${result.case_number}.`);
      void queryClient.invalidateQueries({ queryKey: priorAuthKeys.all });
      void queryClient.invalidateQueries({
        queryKey: ['lmnAttachAvailability', priorAuthRecordId],
      });
      onOpenChange(false);
    },
    onError: (err) => toast.error(`Couldn't attach the letter: ${extractLmnErrorDetail(err)}`),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Attach letter to PA #{caseNumber}</DialogTitle>
            <DialogDescription>
              This signs the letter with your saved e-signature and uploads it to the case.
            </DialogDescription>
          </DialogHeader>

          <SavedSignatureBlock
            signature={signature}
            isLoading={signatureQuery.isLoading}
            isError={signatureQuery.isError}
            onManageSignature={() => setSignaturePanelOpen(true)}
            emptyPrompt="You haven't saved an e-signature yet. Add one and we'll embed it in the letter."
          />

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={attach.isPending}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => attach.mutate()}
              disabled={attach.isPending || signatureQuery.isLoading || signature === null}
              title={signature === null ? 'Add a signature above before signing.' : undefined}
            >
              {attach.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Sign &amp; attach
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reuses the standalone signature flow so the clinician never has to
       *  leave the LMN flow to add or change their signature. */}
      <SignaturePanel open={signaturePanelOpen} onOpenChange={setSignaturePanelOpen} />
    </>
  );
}
